import { jsx as _jsx, jsxs as _jsxs } from "preact/jsx-runtime";
import { useEffect, useMemo, useState } from 'preact/hooks';
import { landingPagesApi } from '@/api/landingPages';
import { ToggleRow } from '@/components/common/ToggleRow';
export function FormFieldsPicker({ selected, onChange, disabled, readOnly }) {
    const [catalog, setCatalog] = useState(null);
    const [error, setError] = useState(null);
    useEffect(() => {
        let cancelled = false;
        landingPagesApi.getFormFieldCatalog().then(res => {
            if (cancelled)
                return;
            if (res.data) {
                setCatalog(res.data);
            }
            else {
                setError(res.error ?? 'Could not load form field catalog');
            }
        });
        return () => { cancelled = true; };
    }, []);
    const requiredKeys = useMemo(() => new Set((catalog?.FormFields ?? []).filter(c => c.IsRequired).map(c => c.CanonicalKey)), [catalog]);
    // Effective selection: explicit selection merged with required keys so required fields stay locked on.
    const effective = useMemo(() => {
        if (selected && selected.length > 0)
            return new Set([...selected, ...requiredKeys]);
        return new Set(requiredKeys);
    }, [selected, requiredKeys]);
    const handleToggle = (canonicalKey, checked) => {
        if (requiredKeys.has(canonicalKey))
            return; // required keys are locked on
        const next = new Set(effective);
        if (checked)
            next.add(canonicalKey);
        else
            next.delete(canonicalKey);
        // Emit in catalog order so downstream display is stable.
        const ordered = (catalog?.FormFields ?? [])
            .map(c => c.CanonicalKey)
            .filter(k => next.has(k));
        onChange(ordered);
    };
    if (error) {
        return _jsx("p", { className: "text-sm text-red-600 dark:text-red-400", children: error });
    }
    if (catalog === null) {
        return (_jsxs("div", { className: "flex items-center gap-2 text-sm text-slate-500 dark:text-slate-400", children: [_jsx("div", { className: "w-3.5 h-3.5 border-2 border-primary-600 border-t-transparent rounded-full animate-spin" }), "Loading form fields\u2026"] }));
    }
    const trackingLabels = catalog.TrackingFields.map(t => t.DisplayLabel).join(', ');
    return (_jsxs("div", { className: "space-y-3", children: [_jsx("div", { className: "space-y-1 divide-y divide-slate-100 dark:divide-slate-700", children: catalog.FormFields.map(field => {
                    const isRequired = field.IsRequired;
                    const isChecked = effective.has(field.CanonicalKey);
                    const isDisabled = disabled || readOnly || isRequired;
                    return (_jsx(ToggleRow, { label: field.DisplayLabel, checked: isChecked, onChange: (v) => handleToggle(field.CanonicalKey, v), disabled: isDisabled, labelAdornment: isRequired && (_jsx("span", { className: "text-[10px] uppercase tracking-wider text-slate-400 dark:text-slate-500", children: "Required" })) }, field.CanonicalKey));
                }) }), catalog.TrackingFields.length > 0 && (_jsxs("div", { className: "rounded-md border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/50 p-3", children: [_jsx("p", { className: "text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400 mb-1", children: "Always captured from URL" }), _jsx("p", { className: "text-xs text-slate-600 dark:text-slate-400 leading-relaxed", children: trackingLabels }), _jsx("p", { className: "text-[11px] text-slate-500 dark:text-slate-500 mt-1 italic", children: "Read from the page URL on submit (if present). Not shown as form inputs." })] }))] }));
}
